import { useState } from 'react';
import { ChevronDown, ChevronRight, ScrollText } from 'lucide-react';
import { getErrorMessage } from '@/lib/api';
import { cn, formatFecha } from '@/lib/cn';
import { useAccionesAuditoria, useAuditoria, type RenglonAuditoria } from '@/lib/queries';
import { Cargando, ErrorCarga, Seleccion, Vacio } from '@/components/ui';

/** Nombres legibles de las acciones. Las que falten se muestran tal cual. */
const ACCIONES: Record<string, string> = {
  'merchant.created': 'Comercio creado',
  'merchant.updated': 'Comercio editado',
  'merchant.status_changed': 'Estado de comercio',
  'merchant.user_created': 'Usuario de comercio creado',
  'user.status_changed': 'Estado de usuario',
  'user.password_reset': 'Contraseña restablecida',
  'courier.created': 'Domiciliario creado',
  'courier.verified': 'Verificación de domiciliario',
  'zone.created': 'Zona creada',
  'zone.updated': 'Zona editada',
  'zone.deleted': 'Zona eliminada',
  'category.created': 'Categoría creada',
  'category.updated': 'Categoría editada',
  'category.deleted': 'Categoría eliminada',
  'settings.updated': 'Parámetros de la plataforma',
  'order.reassigned': 'Pedido reasignado',
  'order.cancelled_by_admin': 'Pedido cancelado por admin',
};

/** Las acciones que conviene mirar dos veces. */
const DELICADAS = new Set([
  'user.status_changed',
  'user.password_reset',
  'order.reassigned',
  'order.cancelled_by_admin',
  'merchant.status_changed',
]);

export function Auditoria() {
  const [accion, setAccion] = useState('');
  const auditoria = useAuditoria({ action: accion || undefined });
  const acciones = useAccionesAuditoria();

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-3xl text-kick-dark">Auditoría</h1>
      <p className="mt-1 text-sm text-kick-gray">
        Quién cambió qué, cuándo, y qué había antes.
      </p>

      <div className="mt-6">
        <Seleccion
          value={accion}
          onChange={(e) => setAccion(e.target.value)}
          aria-label="Filtrar por acción"
          className="w-72"
        >
          <option value="">Todas las acciones</option>
          {acciones.data?.map((a) => (
            <option key={a} value={a}>
              {ACCIONES[a] ?? a}
            </option>
          ))}
        </Seleccion>
      </div>

      {auditoria.isLoading && <Cargando />}
      {auditoria.isError && (
        <ErrorCarga
          mensaje={getErrorMessage(auditoria.error)}
          onReintentar={() => void auditoria.refetch()}
        />
      )}

      {auditoria.data?.data.length === 0 && (
        <div className="mt-6">
          <Vacio
            icono={<ScrollText className="h-6 w-6" />}
            titulo="Nada registrado todavía"
            descripcion="Las acciones administrativas aparecerán aquí en cuanto ocurran."
          />
        </div>
      )}

      <div className="mt-4 space-y-2">
        {auditoria.data?.data.map((renglon) => (
          <Renglon key={renglon.id} renglon={renglon} />
        ))}
      </div>

      {auditoria.data?.hasMore && (
        <p className="mt-4 text-center text-sm text-kick-gray">
          Se muestran los 50 más recientes.
        </p>
      )}
    </div>
  );
}

function Renglon({ renglon }: { renglon: RenglonAuditoria }) {
  const [abierto, setAbierto] = useState(false);
  const hayDetalle = !!renglon.changes?.before || !!renglon.changes?.after;

  return (
    <article
      className={cn(
        'card-kick overflow-hidden',
        DELICADAS.has(renglon.action) && 'border-l-4 border-l-amber-400',
      )}
    >
      <button
        type="button"
        onClick={() => hayDetalle && setAbierto((v) => !v)}
        disabled={!hayDetalle}
        className={cn(
          'flex w-full items-center gap-3 p-3.5 text-left',
          hayDetalle && 'transition hover:bg-kick-light',
        )}
      >
        {hayDetalle ? (
          abierto ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-kick-gray" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-kick-gray" />
          )
        ) : (
          <span className="w-4 shrink-0" />
        )}

        <div className="min-w-0 flex-1">
          <p className="font-semibold text-kick-ink">
            {ACCIONES[renglon.action] ?? renglon.action}
          </p>
          <p className="text-sm text-kick-gray">
            {renglon.actorName ?? 'Sistema'}
            {renglon.actorUsername && ` (${renglon.actorUsername})`}
            {' · '}
            {formatFecha(renglon.createdAt)}
            {renglon.ip && ` · ${renglon.ip}`}
          </p>
        </div>

        <span className="hidden rounded-full bg-kick-light px-2 py-0.5 font-mono text-xs text-kick-gray sm:block">
          {renglon.entityType}
        </span>
      </button>

      {abierto && (
        <div className="grid gap-3 border-t border-kick-gray/10 bg-kick-light/50 p-4 sm:grid-cols-2">
          <Bloque titulo="Antes" datos={renglon.changes?.before} />
          <Bloque titulo="Después" datos={renglon.changes?.after} />
        </div>
      )}
    </article>
  );
}

function Bloque({ titulo, datos }: { titulo: string; datos: unknown }) {
  if (datos === undefined || datos === null) {
    return (
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-kick-gray">
          {titulo}
        </p>
        <p className="text-sm text-kick-gray/70">—</p>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-kick-gray">{titulo}</p>
      <pre className="overflow-x-auto rounded bg-white p-2.5 font-mono text-xs text-kick-ink">
        {JSON.stringify(datos, null, 2)}
      </pre>
    </div>
  );
}
